import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

type JobStatus = 'pending' | 'syncing' | 'success' | 'failed';
type JobType = 'assets' | 'industryJobs' | 'buyOrders' | 'blueprints';

interface SyncJob {
  type: JobType;
  label: string;
}

interface CharacterSyncState {
  characterId: string;
  characterName: string;
  jobs: Record<JobType, JobStatus>;
  errors: Partial<Record<JobType, string>>;
  counts: Partial<Record<JobType, number>>;
}

interface CorporationSyncState {
  corporationId: string;
  corporationName: string;
  jobs: Record<JobType, JobStatus>;
  errors: Partial<Record<JobType, string>>;
  counts: Partial<Record<JobType, number>>;
}

interface SyncAllModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SYNC_JOBS: SyncJob[] = [
  { type: 'assets', label: 'Assets' },
  { type: 'industryJobs', label: 'Industry Jobs' },
  { type: 'buyOrders', label: 'Buy Orders' },
  { type: 'blueprints', label: 'Blueprints' },
];

const createEmptyJobs = (): Record<JobType, JobStatus> => ({
  assets: 'pending',
  industryJobs: 'pending',
  buyOrders: 'pending',
  blueprints: 'pending',
});

export default function SyncAllModal({ isOpen, onClose }: SyncAllModalProps) {
  const [characters, setCharacters] = useState<CharacterSyncState[]>([]);
  const [corporations, setCorporations] = useState<CorporationSyncState[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [currentCharacterIndex, setCurrentCharacterIndex] = useState(0);
  const [currentCorporationIndex, setCurrentCorporationIndex] = useState(-1);
  const [syncingCorporations, setSyncingCorporations] = useState(false);

  // Fetch characters and corporations on open
  useEffect(() => {
    if (isOpen) {
      fetchSyncStatus();
      setIsComplete(false);
      setIsRunning(false);
      setCurrentCharacterIndex(0);
      setCurrentCorporationIndex(-1);
      setSyncingCorporations(false);
    }
  }, [isOpen]);

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/sync/status');
      const { characters: chars, corporations: corps } = response.data;

      setCharacters(
        chars.map((c: { characterId: string; characterName: string }) => ({
          characterId: c.characterId,
          characterName: c.characterName,
          jobs: createEmptyJobs(),
          errors: {},
          counts: {},
        }))
      );

      setCorporations(
        corps.map((c: { corporationId: string; corporationName: string }) => ({
          corporationId: c.corporationId,
          corporationName: c.corporationName,
          jobs: createEmptyJobs(),
          errors: {},
          counts: {},
        }))
      );
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  const syncCharacterJob = useCallback(
    async (characterId: string, jobType: JobType): Promise<{ success: boolean; count?: number; error?: string }> => {
      try {
        const response = await api.post(`/sync/character/${characterId}/${jobType}`);
        return { success: true, count: response.data.count };
      } catch (error: any) {
        return {
          success: false,
          error: error.response?.data?.message || error.message || 'Sync failed',
        };
      }
    },
    []
  );

  const syncCorporationJob = useCallback(
    async (corporationId: string, jobType: JobType): Promise<{ success: boolean; count?: number; error?: string }> => {
      try {
        const response = await api.post(`/sync/corporation/${corporationId}/${jobType}`);
        return { success: true, count: response.data.count };
      } catch (error: any) {
        return {
          success: false,
          error: error.response?.data?.message || error.message || 'Sync failed',
        };
      }
    },
    []
  );

  const runSyncAll = useCallback(async () => {
    setIsRunning(true);
    setIsComplete(false);
    setSyncingCorporations(false);

    // Sync all characters first
    for (let charIdx = 0; charIdx < characters.length; charIdx++) {
      setCurrentCharacterIndex(charIdx);
      const char = characters[charIdx];

      for (let jobIdx = 0; jobIdx < SYNC_JOBS.length; jobIdx++) {
        const job = SYNC_JOBS[jobIdx];

        // Update state to show syncing
        setCharacters((prev) =>
          prev.map((c, i) =>
            i === charIdx
              ? {
                  ...c,
                  jobs: { ...c.jobs, [job.type]: 'syncing' },
                }
              : c
          )
        );

        // Perform the sync
        const result = await syncCharacterJob(char.characterId, job.type);

        // Update state with result
        setCharacters((prev) =>
          prev.map((c, i) =>
            i === charIdx
              ? {
                  ...c,
                  jobs: {
                    ...c.jobs,
                    [job.type]: result.success ? 'success' : 'failed',
                  },
                  errors: result.error
                    ? { ...c.errors, [job.type]: result.error }
                    : c.errors,
                  counts: result.count !== undefined
                    ? { ...c.counts, [job.type]: result.count }
                    : c.counts,
                }
              : c
          )
        );
      }
    }

    // Now sync all corporations
    if (corporations.length > 0) {
      setSyncingCorporations(true);
      setCurrentCharacterIndex(-1);

      for (let corpIdx = 0; corpIdx < corporations.length; corpIdx++) {
        setCurrentCorporationIndex(corpIdx);
        const corp = corporations[corpIdx];

        for (let jobIdx = 0; jobIdx < SYNC_JOBS.length; jobIdx++) {
          const job = SYNC_JOBS[jobIdx];

          // Update state to show syncing
          setCorporations((prev) =>
            prev.map((c, i) =>
              i === corpIdx
                ? {
                    ...c,
                    jobs: { ...c.jobs, [job.type]: 'syncing' },
                  }
                : c
            )
          );

          // Perform the sync
          const result = await syncCorporationJob(corp.corporationId, job.type);

          // Update state with result
          setCorporations((prev) =>
            prev.map((c, i) =>
              i === corpIdx
                ? {
                    ...c,
                    jobs: {
                      ...c.jobs,
                      [job.type]: result.success ? 'success' : 'failed',
                    },
                    errors: result.error
                      ? { ...c.errors, [job.type]: result.error }
                      : c.errors,
                    counts: result.count !== undefined
                      ? { ...c.counts, [job.type]: result.count }
                      : c.counts,
                  }
                : c
            )
          );
        }
      }
    }

    setIsRunning(false);
    setIsComplete(true);
  }, [characters, corporations, syncCharacterJob, syncCorporationJob]);

  const getStatusIcon = (status: 'pending' | 'syncing' | 'success' | 'failed') => {
    switch (status) {
      case 'pending':
        return (
          <div className="w-4 h-4 rounded-full border-2 border-gray-500" />
        );
      case 'syncing':
        return (
          <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
        );
      case 'success':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'failed':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
    }
  };

  const getTotalProgress = () => {
    const totalJobs = (characters.length + corporations.length) * SYNC_JOBS.length;
    const completedCharJobs = characters.reduce(
      (acc, char) =>
        acc +
        Object.values(char.jobs).filter((s) => s === 'success' || s === 'failed').length,
      0
    );
    const completedCorpJobs = corporations.reduce(
      (acc, corp) =>
        acc +
        Object.values(corp.jobs).filter((s) => s === 'success' || s === 'failed').length,
      0
    );
    return totalJobs > 0 ? ((completedCharJobs + completedCorpJobs) / totalJobs) * 100 : 0;
  };

  const getSuccessCount = () => {
    const charSuccess = characters.reduce(
      (acc, char) =>
        acc + Object.values(char.jobs).filter((s) => s === 'success').length,
      0
    );
    const corpSuccess = corporations.reduce(
      (acc, corp) =>
        acc + Object.values(corp.jobs).filter((s) => s === 'success').length,
      0
    );
    return charSuccess + corpSuccess;
  };

  const getFailedCount = () => {
    const charFailed = characters.reduce(
      (acc, char) =>
        acc + Object.values(char.jobs).filter((s) => s === 'failed').length,
      0
    );
    const corpFailed = corporations.reduce(
      (acc, corp) =>
        acc + Object.values(corp.jobs).filter((s) => s === 'failed').length,
      0
    );
    return charFailed + corpFailed;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Sync All</h2>
          {!isRunning && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Progress Bar */}
        {isRunning && (
          <div className="px-6 py-3 border-b border-gray-700">
            <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
              <span>Overall Progress</span>
              <span>{Math.round(getTotalProgress())}%</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${getTotalProgress()}%` }}
              />
            </div>
          </div>
        )}

        {/* Character and Corporation List */}
        <div className="overflow-y-auto max-h-[50vh] p-6">
          {characters.length === 0 && corporations.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              Loading...
            </div>
          ) : (
            <div className="space-y-6">
              {/* Characters Section */}
              {characters.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Characters</h3>
                    {isRunning && !syncingCorporations && (
                      <span className="text-xs text-blue-400">Syncing...</span>
                    )}
                  </div>
                  {characters.map((char, charIdx) => (
                    <div
                      key={char.characterId}
                      className={`bg-gray-750 rounded-lg p-4 border ${
                        isRunning && !syncingCorporations && charIdx === currentCharacterIndex
                          ? 'border-blue-500'
                          : 'border-gray-700'
                      }`}
                    >
                      {/* Character Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={`https://images.evetech.net/characters/${char.characterId}/portrait?size=32`}
                          alt=""
                          className="w-8 h-8 rounded"
                        />
                        <span className="text-white font-medium">{char.characterName}</span>
                      </div>

                      {/* Job Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {SYNC_JOBS.map((job) => (
                          <div
                            key={job.type}
                            className={`flex items-center gap-2 px-3 py-2 rounded ${
                              char.jobs[job.type] === 'syncing'
                                ? 'bg-blue-500/20'
                                : char.jobs[job.type] === 'success'
                                ? 'bg-green-500/10'
                                : char.jobs[job.type] === 'failed'
                                ? 'bg-red-500/10'
                                : 'bg-gray-700/50'
                            }`}
                          >
                            {getStatusIcon(char.jobs[job.type])}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-300 truncate">{job.label}</div>
                              {char.counts[job.type] !== undefined && (
                                <div className="text-xs text-gray-500">
                                  {char.counts[job.type]} items
                                </div>
                              )}
                              {char.errors[job.type] && (
                                <div className="text-xs text-red-400 truncate" title={char.errors[job.type]}>
                                  {char.errors[job.type]}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Corporations Section */}
              {corporations.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Corporations</h3>
                    {isRunning && syncingCorporations && (
                      <span className="text-xs text-blue-400">Syncing...</span>
                    )}
                  </div>
                  {corporations.map((corp, corpIdx) => (
                    <div
                      key={corp.corporationId}
                      className={`bg-gray-750 rounded-lg p-4 border ${
                        isRunning && syncingCorporations && corpIdx === currentCorporationIndex
                          ? 'border-blue-500'
                          : 'border-gray-700'
                      }`}
                    >
                      {/* Corporation Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={`https://images.evetech.net/corporations/${corp.corporationId}/logo?size=32`}
                          alt=""
                          className="w-8 h-8 rounded"
                        />
                        <span className="text-white font-medium">{corp.corporationName}</span>
                      </div>

                      {/* Job Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {SYNC_JOBS.map((job) => (
                          <div
                            key={job.type}
                            className={`flex items-center gap-2 px-3 py-2 rounded ${
                              corp.jobs[job.type] === 'syncing'
                                ? 'bg-blue-500/20'
                                : corp.jobs[job.type] === 'success'
                                ? 'bg-green-500/10'
                                : corp.jobs[job.type] === 'failed'
                                ? 'bg-red-500/10'
                                : 'bg-gray-700/50'
                            }`}
                          >
                            {getStatusIcon(corp.jobs[job.type])}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-300 truncate">{job.label}</div>
                              {corp.counts[job.type] !== undefined && (
                                <div className="text-xs text-gray-500">
                                  {corp.counts[job.type]} items
                                </div>
                              )}
                              {corp.errors[job.type] && (
                                <div className="text-xs text-red-400 truncate" title={corp.errors[job.type]}>
                                  {corp.errors[job.type]}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
          {isComplete ? (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-400">{getSuccessCount()} succeeded</span>
              {getFailedCount() > 0 && (
                <span className="text-red-400">{getFailedCount()} failed</span>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-400">
              {characters.length} character{characters.length !== 1 ? 's' : ''}
              {corporations.length > 0 && `, ${corporations.length} corporation${corporations.length !== 1 ? 's' : ''}`}
              {' - '}
              {(characters.length + corporations.length) * SYNC_JOBS.length} sync operations
            </div>
          )}

          <div className="flex items-center gap-3">
            {!isRunning && !isComplete && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
            )}
            {!isRunning && !isComplete && (
              <button
                onClick={runSyncAll}
                disabled={characters.length === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Start Sync
              </button>
            )}
            {isComplete && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
